import { Tooltip } from 'antd';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { CloudSvg } from '../../../assets/icons/CloudSvg';
import DrawingsSvg from '../../../assets/icons/DrawingsSvg';
import GridSvg from '../../../assets/icons/GridSvg';
import IdeasSvg from '../../../assets/icons/IdeasSvg';
import InstantFeedbackSvg from '../../../assets/icons/InstantFeedbackSvg';
import PodiumSvg from '../../../assets/icons/PodiumSvg';
import { PollSvg } from '../../../assets/icons/PollSvg';
import QuestionsSvg from '../../../assets/icons/QuestionsSvg';
import SlideHeaderSvg from '../../../assets/icons/SlideHeaderSvg';
import { SlideHeaderText } from '../../../assets/icons/SlideHeaderText';
import { SlideImageTextSvg } from '../../../assets/icons/SlideImageTextSvg';
import { SlideTextImageSvg } from '../../../assets/icons/SlideTextImageSvg';
import TitleImageSvg from '../../../assets/icons/TitleImageSvg';
import VotingSvg from '../../../assets/icons/VotingSvg';
import YoutubeSvg from '../../../assets/icons/YoutubeSvg';
import theme from '../../../assets/theme';
import { SlideType as SlideContentType } from '../../../services/backendService/types';
import { RoundCategory } from '../../../utils/roundUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import ConditionalWrapper from '../../_atoms/ConditionalWrapper';
import { Slide, SlideType } from '../../SlideComponents/slide.types';

export const RoundTypeButton = ({
  round,
  button,
  handleRoundTypeChanged,
  onHover,
  onHoverLeave,
}: {
  handleRoundTypeChanged: (type: SlideType) => void;
  round?: Slide;
  button: QuestionButtonsType;
  onHover?: () => void;
  onHoverLeave?: () => void;
}) => {
  const { t } = useTranslation('Host');
  const isDisabled =
    button.disabled ||
    !!(
      button.disabledForRounds &&
      round?.type &&
      button.disabledForRounds.includes(round.type)
    );

  return (
    <ConditionalWrapper
      key={button.text}
      condition={isDisabled}
      truthyWrapper={(children: React.ReactNode) => (
        <Tooltip
          title={
            button.hoverText ? button.hoverText : button.disabled ? t('Coming soon!') : ''
          }
        >
          <DisabledButtonContainer>{children}</DisabledButtonContainer>
        </Tooltip>
      )}
    >
      <Tooltip
        title={toActivitySelectorTooltip(t, button.type as SlideType)}
        mouseEnterDelay={1}
      >
        <TypeButton
          key={button.text}
          $disabled={isDisabled}
          onMouseEnter={onHover}
          onMouseLeave={onHoverLeave}
          onClick={() => !isDisabled && handleRoundTypeChanged(button.type as SlideType)}
        >
          {button.beta && <BetaFlag />}
          <ButtonIconWrapper>{button.icon}</ButtonIconWrapper>
          <p>{button.text}</p>
        </TypeButton>
      </Tooltip>
    </ConditionalWrapper>
  );
};

export const ButtonIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 30px;
  height: 30px;
  & span.anticon {
    width: 30px;
    height: 30px;
  }
  svg {
    height: 30px;
    width: 30px;
    margin: 0;
  }
`;

export const BetaFlag = ({
  dropdown,
  showRightSide,
  top,
  status = 'Beta',
  tagColor = theme.colors.orange,
}: {
  dropdown?: boolean;
  showRightSide?: boolean;
  top?: number;
  status?: string;
  tagColor?: string;
}) => {
  return (
    <BetaContainer $top={top} $isDropdown={dropdown}>
      <Flag
        $isDropdown={dropdown}
        $showRightSide={showRightSide}
        $backgroundColor={tagColor}
      >
        <span>{status}</span>
      </Flag>
    </BetaContainer>
  );
};

const Flag = styled.div<{
  $isDropdown?: boolean;
  $showRightSide?: boolean;
  $backgroundColor?: string;
}>`
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  border-top-right-radius: ${({ $showRightSide }) => ($showRightSide ? '10px' : '0px')};
  border-bottom-right-radius: ${({ $showRightSide }) =>
    $showRightSide ? '10px' : '0px'};
  background: ${({ $backgroundColor }) => $backgroundColor || theme.colors.orange};
  padding: ${({ $showRightSide }) =>
    $showRightSide ? ' 3px 8px 3px 8px' : ' 3px 5px 3px 8px;'};
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  span {
    color: ${theme.colors.black} !important;
    font-weight: bold;
    font-size: 10px;
    line-height: 10px;

    margin-bottom: 0px;
  }

  ${({ $isDropdown }) =>
    $isDropdown &&
    css`
      border-radius: 20px 0px 0px 20px;

      span {
        font-size: 12px;
        padding: 4px 12px 4px 7px;
      }
    `}
`;

const BetaContainer = styled.div<{ $isDropdown?: boolean; $top?: number }>`
  position: absolute;
  top: ${({ $top }) => ($top ? $top : 12)}px;
  right: 0px;
  z-index: ${zIndexes('CREATOR_ROUND_SELECTOR_BETA_LABEL')};

  ${({ $isDropdown }) =>
    $isDropdown &&
    css`
      top: 13px;
    `}
`;

export const TypeButton = styled(Button)<{ $disabled: boolean; $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  padding: 0px;
  height: 70px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.black};
  color: ${theme.colors.black};

  &:hover {
    background-color: rgba(165, 165, 165, 0.1);
    background-color: ${theme.colors.green};
    color: ${theme.colors.white};
  }
  p {
    font-size: 14px;
    margin-top: 5px;
    white-space: normal;
    line-height: 14px;
    color: inherit;
    margin-bottom: 0px;
    font-weight: bold;
    width: 100%;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: rgba(165, 165, 165, 0.1);
      background-color: ${theme.colors.green};
      color: ${theme.colors.white};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      border: 1px solid #a5a5a5 !important;
      color: #a5a5a5;
      background: ${theme.colors.white} !important;
      p {
        color: #a5a5a5;
      }
      svg {
        color: #a5a5a5;
      }
    `}
`;

const toActivitySelectorTooltip = (
  t: TFunction<'Host'>,
  roundType?: SlideType | null,
) => {
  if (roundType === 'answers') return t('Participants answer an open ended question');
  if (roundType === 'aiAssessedAnswers')
    return t('Participants answer an open ended question and get feedback');
  if (roundType === 'canvas') return t('Drawings created by the participants');
  if (roundType === 'poll')
    return t('Participants vote on different options which creates a poll');
  if (roundType === 'wordcloud')
    return t('Participants sends in words and their collection creates a wordcloud');
  return '';
};

const DisabledButtonContainer = styled.div`
  button {
    width: 100%;
  }
`;

export const toRoundTypeSelectorTitle = (
  t: TFunction<'Host'>,
  roundCategory?: RoundCategory,
) => {
  if (roundCategory === 'response') return t('Activities');
  if (roundCategory === 'collaboration') return t('Collaboration');
  if (roundCategory === 'results') return t('Discussion views');
  if (roundCategory === 'slide') return t('Instructional slides');
  return '';
};

export const toRoundTypeSelectorTooltip = (
  t: TFunction<'Host'>,
  roundCategory?: RoundCategory,
) => {
  if (roundCategory === 'response')
    return t('Get real-time input from your participants with these activities');
  if (roundCategory === 'collaboration')
    return t('What should your participants vote for?');
  if (roundCategory === 'results')
    return t('The results from voting of responses will be visualised using this option');
  if (roundCategory === 'slide')
    return t('Use instructional slides to help guide your activities');
  return '';
};

export type QuestionButtonsType = {
  type: SlideType | SlideContentType | null;
  text: string;
  icon: React.ReactNode;
  responseCategory: string;
  disabled: boolean;
  beta?: boolean;
  disabledForRounds?: string[];
  hoverText?: string;
};

export const RoundTypeButtons = (
  t: TFunction<'Host'>,
): {
  slide: QuestionButtonsType[];
  response: QuestionButtonsType[];
  collaboration: QuestionButtonsType[];
  results: QuestionButtonsType[];
} => {
  return {
    slide: [
      {
        type: 'header',
        text: t('Header'),
        icon: <SlideHeaderSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'image',
        text: t('Title & media'),
        icon: <TitleImageSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'header_text',
        text: t('Text'),
        icon: <SlideHeaderText />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'text_image',
        text: t('Text & media'),
        icon: <SlideTextImageSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'image_text',
        text: t('Media & text'),
        icon: <SlideImageTextSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'fullscreen_media',
        text: t('Fullscreen media'),
        icon: <YoutubeSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
    ],
    response: [
      {
        type: 'poll',
        text: t('Poll'),
        icon: <PollSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'wordcloud',
        text: t('Word cloud'),
        icon: <CloudSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'answers',
        text: t('Open question'),
        icon: <QuestionsSvg />,
        responseCategory: 'popular',
        disabled: false,
      },
      {
        type: 'aiAssessedAnswers',
        text: 'AI Feedback',
        icon: <InstantFeedbackSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'canvas',
        text: t('Drawings'),
        icon: <DrawingsSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
    ],
    collaboration: [
      {
        type: 'voting',
        text: t('Voting'),
        icon: <VotingSvg />,
        responseCategory: 'collaboration',
        disabled: false,
      },
      {
        type: null,
        text: t('Group voting'),
        icon: <IdeasSvg />,
        responseCategory: 'collaboration',
        disabled: true,
      },
    ],
    results: [
      {
        type: 'podium',
        text: t('Top 5'),
        icon: <PodiumSvg />,
        responseCategory: 'results',
        disabled: false,
        disabledForRounds: ['responsegrid'],
      },
      {
        type: 'responsegrid',
        text: t('Grid'),
        icon: <GridSvg />,
        responseCategory: 'results',
        disabled: false,
        beta: true,
        disabledForRounds: ['podium'],
        hoverText: t('Disable voting to use grid display'),
      },
    ],
  };
};

export const BackTitleGrid = styled.span`
  display: grid;
  grid-template-columns: 70px auto 70px;
  align-items: center;
  width: 100%;
  h1 {
    font-size: 24px !important;
    text-align: center;
  }

  @media (max-width: 900px) {
    grid-template-columns: 20px auto 20px;
    h1 {
      font-size: 16px !important;
    }
  }
  margin-bottom: 10px;
`;
